import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  Grid,
  Tabs,
  Tab,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  TextField,
  Paper,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Note as NoteIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  Person as PersonIcon
} from '@mui/icons-material';

const KYCVerification = () => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVerifications();
  }, [currentPage, filterStatus]);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `/verification/admin/pending?page=${currentPage}&limit=20&status=${filterStatus}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setVerifications(response.data.data.verifications);
      setTotalPages(response.data.data.totalPages);
      setPendingCount(response.data.data.totalVerifications);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      setError('Failed to fetch verifications');
      setLoading(false);
    }
  };

  const fetchVerificationDetails = async (verificationId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `/verification/admin/${verificationId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSelectedVerification(response.data.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching verification details:', error);
      setError('Error loading verification details');
    }
  };

  const handleVideoReview = async (verificationId, status) => {
    let rejectionReason = null;
    if (status === 'rejected') {
      rejectionReason = prompt('Please enter rejection reason:');
      if (!rejectionReason) return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `/verification/admin/video/${verificationId}`,
        {
          status,
          rejectionReason,
          adminNote: `Video verification ${status} by admin`
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert(`Video verification ${status} successfully!`);
      fetchVerifications();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error reviewing video:', error);
      alert(error.response?.data?.message || 'Error reviewing video');
    }
  };

  const handleDocumentReview = async (verificationId, status) => {
    let rejectionReason = null;
    if (status === 'rejected') {
      rejectionReason = prompt('Please enter rejection reason:');
      if (!rejectionReason) return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `/verification/admin/documents/${verificationId}`,
        {
          status,
          rejectionReason,
          adminNote: `Document verification ${status} by admin`
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert(`Document verification ${status} successfully!`);
      fetchVerifications();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error reviewing documents:', error);
      alert(error.response?.data?.message || 'Error reviewing documents');
    }
  };

  const handleAddNote = async (verificationId) => {
    const note = prompt('Enter your note:');
    if (!note) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `/verification/admin/${verificationId}/note`,
        { note },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('Note added successfully!');
      fetchVerificationDetails(verificationId);
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Error adding note');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      case 'under_review':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              KYC Verification
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and verify user documents
            </Typography>
          </Box>
          <Chip
            label={`${pendingCount} Pending Reviews`}
            color="warning"
            size="large"
            sx={{ fontWeight: 600, px: 2, py: 3 }}
          />
        </Box>
      </Paper>

      {/* Filters */}
      <Paper elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={filterStatus}
          onChange={(e, newValue) => {
            setFilterStatus(newValue);
            setCurrentPage(1);
          }}
          sx={{ px: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" value="all" />
          <Tab label="Under Review" value="under_review" />
          <Tab label="Approved" value="approved" />
          <Tab label="Rejected" value="rejected" />
        </Tabs>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Verification Cards */}
      <Grid container spacing={3}>
        {verifications.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary">
                No verifications found
              </Typography>
            </Paper>
          </Grid>
        ) : (
          verifications.map((verification) => (
            <Grid item xs={12} key={verification._id}>
              <Card elevation={3} sx={{ borderRadius: 2, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                <CardContent>
                  {/* User Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          fontSize: '1.5rem',
                          fontWeight: 600
                        }}
                      >
                        {verification.userId?.name?.charAt(0).toUpperCase() || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {verification.userId?.casualProfileName|| 'Unknown User'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {verification.userId?.email}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={verification.overallStatus.replace('_', ' ')}
                      color={getStatusColor(verification.overallStatus)}
                      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Info Grid */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Submitted
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDate(verification.createdAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Documents
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {verification.documentVerification?.documents?.length || 0} files
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Video Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={verification.videoVerification?.status || 'Not Submitted'}
                          color={getStatusColor(verification.videoVerification?.status)}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Document Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={verification.documentVerification?.status || 'Not Submitted'}
                          color={getStatusColor(verification.documentVerification?.status)}
                          size="small"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => {
                      if (verification.videoVerification?.status === 'pending') {
                        handleVideoReview(verification._id, 'approved');
                      } else if (verification.documentVerification?.status === 'pending') {
                        handleDocumentReview(verification._id, 'approved');
                      }
                    }}
                    disabled={
                      verification.videoVerification?.status !== 'pending' &&
                      verification.documentVerification?.status !== 'pending'
                    }
                    fullWidth
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      if (verification.videoVerification?.status === 'pending') {
                        handleVideoReview(verification._id, 'rejected');
                      } else if (verification.documentVerification?.status === 'pending') {
                        handleDocumentReview(verification._id, 'rejected');
                      }
                    }}
                    disabled={
                      verification.videoVerification?.status !== 'pending' &&
                      verification.documentVerification?.status !== 'pending'
                    }
                    fullWidth
                  >
                    Reject
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => fetchVerificationDetails(verification._id)}
                    fullWidth
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Detail Modal */}
      <Dialog
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={600}>
            Verification Details
          </Typography>
          <IconButton onClick={() => setShowDetailModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedVerification && (
            <Stack spacing={3}>
              {/* User Information */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon /> User Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Name
                    </Typography>
                    <Typography variant="body1">{selectedVerification.userId?.casualProfileName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Email
                    </Typography>
                    <Typography variant="body1">{selectedVerification.userId?.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Mobile
                    </Typography>
                    <Typography variant="body1">{selectedVerification.userId?.mobileNumber || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Gender
                    </Typography>
                    <Typography variant="body1">{selectedVerification.userId?.gender || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Video Verification */}
              {selectedVerification.videoVerification && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VideoIcon /> Video Verification
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ mb: 2 }}>
                    <video
                      controls
                      style={{ width: '100%', maxHeight: '400px', borderRadius: '12px', backgroundColor: '#000' }}
                      src={selectedVerification.videoVerification.videoUrl}
                    >
                      Your browser does not support video playback.
                    </video>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedVerification.videoVerification.status}
                          color={getStatusColor(selectedVerification.videoVerification.status)}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Uploaded
                      </Typography>
                      <Typography variant="body1">{formatDate(selectedVerification.videoVerification.uploadedAt)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Duration
                      </Typography>
                      <Typography variant="body1">{selectedVerification.videoVerification.videoDuration}s</Typography>
                    </Grid>
                    {selectedVerification.videoVerification.rejectionReason && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Rejection Reason
                        </Typography>
                        <Typography variant="body1" color="error" fontWeight={600}>
                          {selectedVerification.videoVerification.rejectionReason}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {selectedVerification.videoVerification.status === 'pending' && (
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleVideoReview(selectedVerification._id, 'approved')}
                        fullWidth
                      >
                        Approve Video
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => handleVideoReview(selectedVerification._id, 'rejected')}
                        fullWidth
                      >
                        Reject Video
                      </Button>
                    </Stack>
                  )}
                </Paper>
              )}

              {/* Document Verification */}
              {selectedVerification.documentVerification && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DocumentIcon /> Document Verification
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedVerification.documentVerification.status}
                          color={getStatusColor(selectedVerification.documentVerification.status)}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Document Type
                      </Typography>
                      <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                        {selectedVerification.documentVerification.documentType?.replace('_', ' ')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Uploaded
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedVerification.documentVerification.uploadedAt)}
                      </Typography>
                    </Grid>
                    {selectedVerification.documentVerification.rejectionReason && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Rejection Reason
                        </Typography>
                        <Typography variant="body1" color="error" fontWeight={600}>
                          {selectedVerification.documentVerification.rejectionReason}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Grid container spacing={2}>
                    {selectedVerification.documentVerification.documents?.map((doc, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            transition: 'all 0.3s',
                            '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                          }}
                        >
                          <Box
                            component="a"
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: 'none', display: 'block' }}
                          >
                            {doc.fileType === 'pdf' ? (
                              <Box
                                sx={{
                                  height: 200,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'grey.100'
                                }}
                              >
                                <DocumentIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mt: 1 }}>
                                  PDF Document
                                </Typography>
                              </Box>
                            ) : (
                              <Box
                                component="img"
                                src={doc.fileUrl}
                                alt={`Document ${index + 1}`}
                                sx={{ width: '100%', height: 200, objectFit: 'cover' }}
                              />
                            )}
                            <Box sx={{ p: 1, bgcolor: 'background.paper', textAlign: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {doc.fileType.toUpperCase()} • {(doc.fileSize / 1024).toFixed(2)} KB
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  {selectedVerification.documentVerification.status === 'pending' && (
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleDocumentReview(selectedVerification._id, 'approved')}
                        fullWidth
                      >
                        Approve Documents
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => handleDocumentReview(selectedVerification._id, 'rejected')}
                        fullWidth
                      >
                        Reject Documents
                      </Button>
                    </Stack>
                  )}
                </Paper>
              )}

              {/* Admin Notes */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NoteIcon /> Admin Notes
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<NoteIcon />}
                    onClick={() => handleAddNote(selectedVerification._id)}
                  >
                    Add Note
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {selectedVerification.adminNotes?.length > 0 ? (
                    selectedVerification.adminNotes.map((note, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 2, borderLeft: 4, borderColor: 'primary.main' }}>
                        <Typography variant="body1" gutterBottom>
                          {note.note}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          By {note.addedBy?.name} • {formatDate(note.addedAt)}
                        </Typography>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, fontStyle: 'italic' }}>
                      No notes added yet
                    </Typography>
                  )}
                </Stack>
              </Paper>

              {/* Metadata */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Metadata
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      IP Address
                    </Typography>
                    <Typography variant="body2">{selectedVerification.ipAddress || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Device Info
                    </Typography>
                    <Typography variant="body2">{selectedVerification.deviceInfo || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      User Agent
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                      {selectedVerification.userAgent || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default KYCVerification;